<template>
  <div v-if="show" class="terms-modal-overlay" @click.self="handleOverlayClick">
    <div class="terms-modal">
      <div class="terms-modal-header">
        <h3 class="terms-modal-title">
          <i class="fas fa-file-contract me-2"></i>
          Términos de Servicio
        </h3>
        <button type="button" class="terms-modal-close" @click="handleClose">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="terms-modal-body">
        <ContentSkeleton v-if="loading" variant="lines" :rows="8" padded />
        <div v-else-if="termsText" class="terms-content" v-html="termsText"></div>
        <div v-else class="text-center py-4 text-muted">
          <p>No hay términos de servicio configurados.</p>
        </div>
      </div>
      <div class="terms-modal-footer">
        <div class="form-check mb-3 terms-checkbox-container">
          <input
            class="form-check-input terms-checkbox-large"
            type="checkbox"
            id="acceptTermsCheckbox"
            v-model="accepted"
            :disabled="loading || !termsText"
          />
          <label class="form-check-label terms-checkbox-label" for="acceptTermsCheckbox">
            <strong>He leído y acepto los términos de servicio</strong>
          </label>
        </div>
        <div class="d-flex justify-content-end gap-2">
          <button
            v-if="canClose"
            type="button"
            class="btn btn-secondary"
            @click="handleClose"
          >
            Cerrar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleAccept"
            :disabled="!accepted || loading || !termsText"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin me-1"></i>
            Aceptar y Continuar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import { logoutAction } from "@/pages/Auth/actions/login.action";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "TermsOfServiceModal",
  components: { ContentSkeleton },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    termsText: {
      type: String,
      default: "",
    },
    canClose: {
      type: Boolean,
      default: false, // Si es false, el usuario debe aceptar para cerrar
    },
  },
  data() {
    return {
      accepted: false,
      loading: false,
    };
  },
  methods: {
    handleAccept() {
      if (!this.accepted || this.loading) return;

      this.loading = true;
      const token = localStorage.getItem("token");

      wencesApi
        .post(
          "/api/users/accept-terms",
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then(() => {
          this.$emit("accepted");
          this.accepted = false;
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error accepting terms:", error);
          this.$toast?.error?.(
            error.response?.data?.error ||
              "Error al aceptar los términos de servicio"
          );
          this.loading = false;
        });
    },
    async handleClose() {
      // Si no se ha aceptado, cerrar sesión
      if (!this.accepted) {
        await this.logout();
        return;
      }
      
      if (!this.canClose) {
        // Si no se puede cerrar sin aceptar, cerrar sesión
        await this.logout();
        return;
      }
      
      this.$emit("close");
      this.accepted = false;
    },
    async handleOverlayClick() {
      // Si hace click fuera y no ha aceptado, cerrar sesión
      if (!this.accepted) {
        await this.logout();
        return;
      }
      
      if (this.canClose) {
        this.handleClose();
      } else {
        // Si no se puede cerrar sin aceptar, cerrar sesión
        await this.logout();
      }
    },
    async logout() {
      try {
        await logoutAction();
        // Limpiar también pendingRoute si existe
        localStorage.removeItem("pendingRoute");
        // Emitir evento para que el componente padre sepa que se cerró sesión
        this.$emit("logout");
        // Redirigir al login
        if (this.$router) {
          this.$router.push("/login");
        } else {
          window.location.href = "/login";
        }
      } catch (error) {
        console.error("Error during logout:", error);
        // Aun así, limpiar localStorage y redirigir
        localStorage.clear();
        if (this.$router) {
          this.$router.push("/login");
        } else {
          window.location.href = "/login";
        }
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.accepted = false;
      }
    },
  },
};
</script>

<style scoped>
.terms-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.terms-modal {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.terms-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
}

.terms-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.terms-modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  transition: color 0.2s;
}

.terms-modal-close:hover {
  color: #333;
}

.terms-modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 300px;
  max-height: calc(90vh - 200px);
}

.terms-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
  background: white;
  padding: 1rem;
  border-radius: 4px;
}

.terms-content p {
  margin-bottom: 1rem;
}

.terms-content h1,
.terms-content h2,
.terms-content h3,
.terms-content h4,
.terms-content h5,
.terms-content h6 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.terms-content h1 {
  font-size: 1.75rem;
}

.terms-content h2 {
  font-size: 1.5rem;
}

.terms-content h3 {
  font-size: 1.25rem;
}

.terms-content ul,
.terms-content ol {
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

.terms-content li {
  margin-bottom: 0.5rem;
}

.terms-content strong {
  font-weight: 600;
}

.terms-content em {
  font-style: italic;
}

.terms-modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.gap-2 {
  gap: 0.5rem;
}

.terms-checkbox-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 2px solid #dee2e6;
}

.terms-checkbox-large {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  cursor: pointer;
  flex-shrink: 0;
}

.terms-checkbox-label {
  font-size: 1.1rem;
  cursor: pointer;
  margin: 0;
  user-select: none;
  color: #000;
}

.terms-checkbox-label strong {
  font-weight: 600;
  color: #000;
}

#darkdesign .terms-checkbox-container {
  background: var(--dark-bg-tertiary, #242a33);
  border-color: var(--dark-border-color, #2f3943);
}

#darkdesign .terms-checkbox-label {
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-checkbox-label strong {
  color: var(--dark-text-primary, #e7e9ea);
}

/* =========================================
   Dark Mode Styles
   ========================================= */

#darkdesign .terms-modal-overlay {
  background-color: rgba(0, 0, 0, 0.7);
}

#darkdesign .terms-modal {
  background: var(--dark-bg-card, #1a1f26);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.6);
  border: 1px solid var(--dark-border-color, #2f3943);
}

#darkdesign .terms-modal-header {
  background: var(--dark-bg-secondary, #1a1f26);
  border-bottom-color: var(--dark-border-color, #2f3943);
}

#darkdesign .terms-modal-title {
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-modal-close {
  color: var(--dark-text-secondary, #8b98a5);
}

#darkdesign .terms-modal-close:hover {
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-modal-body {
  background: var(--dark-bg-card, #1a1f26);
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-content {
  color: #333 !important;
  background: white !important;
}

#darkdesign .terms-content h1,
#darkdesign .terms-content h2,
#darkdesign .terms-content h3,
#darkdesign .terms-content h4,
#darkdesign .terms-content h5,
#darkdesign .terms-content h6 {
  color: #333 !important;
}

#darkdesign .terms-content p {
  color: #333 !important;
}

#darkdesign .terms-content li {
  color: #333 !important;
}

#darkdesign .terms-content strong {
  color: #333 !important;
}

#darkdesign .terms-content em {
  color: #333 !important;
}

#darkdesign .terms-content a {
  color: #0066cc !important;
}

#darkdesign .terms-content a:hover {
  color: #0052a3 !important;
}

#darkdesign .terms-content code {
  background: #f5f5f5 !important;
  color: #333 !important;
  border: 1px solid #ddd !important;
}

#darkdesign .terms-content pre {
  background: #f5f5f5 !important;
  color: #333 !important;
  border: 1px solid #ddd !important;
}

#darkdesign .terms-content blockquote {
  border-left-color: #ddd !important;
  color: #666 !important;
}

#darkdesign .terms-modal-footer {
  background: var(--dark-bg-secondary, #1a1f26);
  border-top-color: var(--dark-border-color, #2f3943);
}

#darkdesign .terms-modal-footer .form-check-label {
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-modal-footer .form-check-input {
  background-color: var(--dark-bg-input, #242a33);
  border-color: var(--dark-border-color, #2f3943);
}

#darkdesign .terms-modal-footer .form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

#darkdesign .terms-modal-footer .btn-secondary {
  background-color: var(--dark-bg-tertiary, #242a33);
  border-color: var(--dark-border-color, #2f3943);
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-modal-footer .btn-secondary:hover:not(:disabled) {
  background-color: var(--dark-bg-hover, #2d343f);
  border-color: var(--dark-border-light, #38444d);
  color: var(--dark-text-primary, #e7e9ea);
}

#darkdesign .terms-modal-footer .btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

#darkdesign .terms-modal-footer .btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
  color: white;
}

#darkdesign .terms-modal-footer .btn-primary:hover:not(:disabled) {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

#darkdesign .terms-modal-footer .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

#darkdesign .terms-modal-body .text-muted {
  color: var(--dark-text-muted, #6e7a88) !important;
}

/* Scrollbar styles for dark mode */
#darkdesign .terms-modal-body::-webkit-scrollbar {
  width: 8px;
}

#darkdesign .terms-modal-body::-webkit-scrollbar-track {
  background: var(--dark-bg-secondary, #1a1f26);
}

#darkdesign .terms-modal-body::-webkit-scrollbar-thumb {
  background: var(--dark-bg-active, #363e4a);
  border-radius: 4px;
}

#darkdesign .terms-modal-body::-webkit-scrollbar-thumb:hover {
  background: var(--dark-text-muted, #6e7a88);
}
</style>

